import { Subject } from 'rxjs';
import { MtnMapOptions } from './mtn-map-options';
import {
  MapBoundsChangeEvent,
  MapClickEvent,
  MapDrawEvent,
  MapEvent,
  MapFilterChangeEvent,
  MapOptionChangeEvent,
  MapSelectionEvent,
  MapSelectionModeChangeEvent,
  MapTypeChangeEvent
} from './map-events';
import { Layer, LayerType } from './layer';
import { MapSnapshot } from './map-snapshot';
import { KeyListener } from '../core/util/key-listener';
import { GeometryUtil } from '../core/util/geometry-util';
// import { MapStyleService } from './style/map-style.service';
import * as _ from 'lodash';
import { MarkerUtil } from './marker/marker-util';
import { MarkerZIndexUtil } from './marker/marker-z-index-util';
import { MarkerIconUtil } from './marker/marker-icon-util';
import { MapSelectionMode } from './map-selection-mode.enum';
import { AbstractControl } from '@angular/forms';
import { milesToMeters } from '../core/util/math-utils';
import { FeatureType } from '../core/federation/feature/feature-type.enum';
import { Feature, FeatureCollection } from '../core/models';

import Polygon = google.maps.Polygon;
import MapMouseEvent = google.maps.MapMouseEvent;
import PlacesService = google.maps.places.PlacesService;
import Marker = google.maps.Marker;
import Polyline = google.maps.Polyline;
import LatLng = google.maps.LatLng;
import Circle = google.maps.Circle;
import MapsEventListener = google.maps.MapsEventListener;
import LatLngLiteral = google.maps.LatLngLiteral;

export class MtnMap extends google.maps.Map {

  private _events$ = new Subject<MapEvent<any>>();
  _mapId: string;

  private _keyListener = new KeyListener();
  private _mousemoveEventListener: MapsEventListener;
  private _mousedownEventListener: MapsEventListener;
  private _mouseupEventListener: MapsEventListener;
  private _options: MtnMapOptions;
  private _placesService: PlacesService;
  private _ringCircle: Circle;
  private _selectionMode: MapSelectionMode;
  private _ringMiles: MapSelectionMode;
  public _isSelectAll: any;
  // private _styleService: MapStyleService;
  private _isStoreAdding:boolean = false;
  private _isMeasurementEnabled:boolean = false;
  private _featureHistory = new Map<string, LatLngLiteral>();
  private _layers: Layer[] = [];
  private _selectedFeatures = new Map<string, Feature>();
  private _selections = new Set<string>();
  private _polygonArea: number = 0;
  private _lastSelectionMode: string = '';
  constructor(element: HTMLElement, snapshot: MapSnapshot, mapId?: string,
  ) {
    super(element, {
      ...snapshot.options,
      mapId: mapId ?? undefined, // Ensure mapId is applied in MapOptions
      renderingType: google.maps.RenderingType.RASTER // Disable WebGL, use raster rendering
    });
    this._mapId = mapId ?? '';
    this.initMap(snapshot);
  }
   get mapId(): string {
    return this._mapId;
  }


  get bounds(): FeatureCollection {
    const bounds = this.getBounds()?.toJSON();
    if (bounds) {
      const polygon = new Polygon({
        paths: [
          {lat: bounds.north, lng: bounds.east},
          {lat: bounds.north, lng: bounds.west},
          {lat: bounds.south, lng: bounds.west},
          {lat: bounds.south, lng: bounds.east}
        ]
      });

      const feature = GeometryUtil.googlePolygonToGeoJsonFeature(polygon);

      const featureCollection = new FeatureCollection();
      featureCollection.features.push(feature);
      return featureCollection;
    } else {
      return null;
    }
  }

  get events(): Subject<MapEvent<any>> {
    return this._events$;
  }

  get featureHistory(): Map<string, LatLngLiteral> {
    return this._featureHistory;
  }

  get layers(): Layer[] {
    return this._layers;
  }

  get markers(): Marker[] {
    return (<Marker[]>this.findLayer(LayerType.MARKER)?.objects) || [];
  }

  get storeAdding(){
    return this._isStoreAdding
  }

  get measurementEnabled(){
    return this._isMeasurementEnabled
  }

  get options(): MtnMapOptions {
    return this._options;
  }

  get selectionMode(): MapSelectionMode {
    return this._selectionMode;
  }
  get ringMiles(): MapSelectionMode {
    return this._ringMiles;
  }

  get isSelectAll(): any {
    return this._isSelectAll;
  }

  get selectedFeatures(): Feature[] {
    return Array.from(this._selectedFeatures.values());
  }

  get selectedFeatureUuids(): string[] {
    return this.selectedFeatures.map((feature: Feature) => feature.id);
  }

  get selections(): Set<string> {
    return this._selections;
  }

  get polygonArea(): number {
    return this._polygonArea;
  }

  get lastSelectionMode(): string {
    return this._lastSelectionMode;
  }

  get snapshot(): MapSnapshot {
    return new MapSnapshot({
      bounds: this.getBounds()?.toJSON(),
      center: this.getCenter()?.toJSON(),
      options: _.cloneDeep(this.options),
      selections: this._selections,
      zoom: this.getZoom(),
      polygonArea: this._polygonArea,
      selectionMode: this._lastSelectionMode
    });
  }

  clearSelections(): void {
    this._selections.clear();
    this._selectedFeatures.clear();
  }

  disableDrawing(): void {
    // No-op kept for public API. DrawingManager was removed from Google Maps
    // JS API v3.65; the lasso/ring drawing flows manage their own polyline/
    // circle and mouse listeners, so nothing has to be torn down here.
  }

  drawMarkers(features: Feature[],map:any): Marker[] {
    const relevantFeatures = MarkerUtil.getRelevantFeatures(features);
    /*Keep a history of all features that we've generated markers for, so we can find selections after they leave the map*/
    relevantFeatures.forEach((feature: Feature) => {
      this._featureHistory.set(feature.id, feature.getGeometryAsLatLng());
    });

    const selectedFeatures = _.filter(relevantFeatures, (feature: Feature) => this._selections.has(feature.id));
    const remainingFeatures = _.reject(relevantFeatures, (feature: Feature) => this._selections.has(feature.id));

    const markers = MarkerUtil.buildMarkers(remainingFeatures, false, this._options,map);
    const selectedMarkers = MarkerUtil.buildMarkers(selectedFeatures, true, this._options,map);

    this.findLayer(LayerType.MARKER).addAll(markers);
    this.findLayer(LayerType.MARKER).addAll(selectedMarkers);

    const allMarkers = [...markers, ...selectedMarkers];

    this._events$.next(new MapDrawEvent(allMarkers));

    return allMarkers;
  }

  enableDrawing(): void {
    // No-op kept for public API. See disableDrawing() for context.
  }

  /**
   * At the time of this writing, only markers can have ids set, so this only searches the marker layers.
   */
  findFeature(id: string): Feature {
    const features = this.findLayer(LayerType.MARKER).objects.map((marker: Marker) => marker['feature']);
    return _.find(features, (feature: Feature) => feature.id === id);
  }

  findFeatures(ids: string[]): Feature[] {
    const features = this.findLayer(LayerType.MARKER).objects.map((marker: Marker) => marker['feature']);
    return _.filter(features, (feature: Feature) => _.includes(ids, feature.id));
  }

  findLayer(type: LayerType): Layer {
    return _.find(this.layers, (layer: Layer) => layer.type === type);
  }

  /**
   * This doesn't return the full features, just a list of points that represent the selected features, so the map can
   * be recentered (and thus redrawn with full features).
   */
  findSelectedPoints(): LatLngLiteral[] {
    const points: LatLngLiteral[] = [];

    this._selections.forEach((selection: string) => {
      const point = this._featureHistory.get(selection);
      if (point) {
        points.push(point);
      }
    });

    return points;
  }

  isFeatureType(type: FeatureType): boolean {
    return this._options.featureType === type;
  }

  isSelected(id: string): boolean {
    return this._selections.has(id);
  }

  removeSelection(id: string, triggerEvent = false) {
    this._selections.delete(id);
    this._selectedFeatures.delete(id);

    if (triggerEvent) {
      this.updateMarkers();
      this._events$.next(new MapSelectionEvent(this._selections));
    }
  }

  select(id: string): void {
    const isCtrlSelection = !!this._keyListener.ctrl;

    if (!isCtrlSelection && this._selectionMode !== MapSelectionMode.MULTI_SELECT) {
      this.clearSelections();
    }

    const isExistingSelection = this._selections.has(id);
    this.toggleSelection(id, isCtrlSelection, isExistingSelection);

    /*Set multi-select if ctrlSelection and we've selected now more than one*/
    if (isCtrlSelection && this._selections.size > 1 && !this._selectionMode) {
      this.setSelectionMode(MapSelectionMode.MULTI_SELECT);
    }

    this.updateMarkers();
    this._events$.next(new MapSelectionEvent(this._selections));
  }

  selectAll(ids: string[]): void {
    const isCtrlSelection = !!this._keyListener.ctrl;

    if (!isCtrlSelection) {
      this.clearSelections();
    }

    const isBatchExisting = this._selections.has(ids[0]);
    ids.forEach((id: string) => {
      this.toggleSelection(id, isCtrlSelection, isBatchExisting);
    });

    this.updateMarkers();
    this._events$.next(new MapSelectionEvent(this._selections));
  }

  /**
   * Applies, not sets, the given options to the existing map options
   * param options
   */
  setOptions(options: MtnMapOptions) {
    console.log("🚀 ~ MtnMap ~ setOptions ~ options:", options)
    /*
    Check for specific changes, so we can emit appropriate events later
     */
    const isFiltersChanged = !!options.filterGroup || !!options.staticFeatures;
    const isMapTypeChanged = this._options.mapTypeId !== options.mapTypeId;

    /*
    Apply changes
     */

    /*Translate our control configurations (that apply to the google maps controls) to the google options*/
    if (options.controlConfiguration?.isStreetViewEnabled !== undefined) {
      options.streetViewControl = options.controlConfiguration.isStreetViewEnabled;
    }
    if (options.controlConfiguration?.isTiltAndRotateEnabled !== undefined) {
      options.rotateControl = options.controlConfiguration.isTiltAndRotateEnabled;
    }

    Object.assign(this._options, options);
    //legacy style stop due migrated to  cloud style
    // this._options.styles = this._styleService.buildStyles(this._options);
    super.setOptions(this._options);

    /*
    Emit change events
     */
    this._events$.next(new MapOptionChangeEvent(this.options));

    if (isFiltersChanged) {
      this._events$.next(new MapFilterChangeEvent(this._options.filterGroup));
    }

    if (isMapTypeChanged) {
      this._events$.next(new MapTypeChangeEvent(this._options.mapTypeId));
    }
  }

  setSelectionMode(mode: MapSelectionMode, radiusMilesFormControl?: AbstractControl): void {
    this._selectionMode = mode;
    this.resetDrawingOptions();

    switch (mode) {
      case MapSelectionMode.LASSO:
        this.handleLassoSelection();
        break;
      case MapSelectionMode.MULTI_SELECT:
        /*No special handling needed here, but we also don't want to call the default logic*/
        break;
      case MapSelectionMode.RING:
        this.handleRingSelection(radiusMilesFormControl);
        break;
      default:
        break;
    }

    this._events$.next(new MapSelectionModeChangeEvent(mode));
  }

  private addSelection(id: string): void {
    this._selections.add(id);

    const selectedFeature = this.findFeature(id);
    if (selectedFeature) {
      this._selectedFeatures.set(id, selectedFeature);
    }
  }

  clearMarkers(): void {
    this.findLayer(LayerType.MARKER).clear();
  }

  private disableMapDragAndScroll(): void {
    this.setOptions({
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: false,
      clickableIcons: false
    });
  }

  private disableMarkerClick(): void {
    this.setOptions({
      draggableCursor: 'crosshair',
      clickableIcons: false
    });

    this.findLayer(LayerType.MARKER).objects.forEach((marker: Marker) => {
      marker['clickable'] = false;
      marker['cursor'] = 'crosshair';
    });
  }

  private enableMapDragAndScroll(): void {
    this.setOptions({
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: true,
      clickableIcons: true
    });
  }

  private enableMarkerClick(): void {
    this.setOptions({
      draggableCursor: '',
      clickableIcons: true
    });

    this.findLayer(LayerType.MARKER).objects.forEach((marker: Marker) => {
      marker['Clickable'] = true;
      marker['cursor'] = '';
    });
  }

  private handleLassoSelection(): void {
    this.disableMapDragAndScroll();

    const polyline = new Polyline({
      map: this,
      clickable: false,
      strokeColor: 'limegreen'
    });

    // Match the previous DrawingManager behavior: only grow the polyline while
    // the user is actively pressing the mouse button. Without this gate, the
    // mousemove listener would start tracking the cursor as soon as lasso mode
    // is enabled, drawing a stray line before the user has clicked.
    this._mousedownEventListener = google.maps.event.addListenerOnce(this, 'mousedown', (downEvent: MapMouseEvent) => {
      polyline.getPath().push(downEvent.latLng);

      this._mousemoveEventListener = google.maps.event.addListener(this, 'mousemove', (event: MapMouseEvent) => {
        polyline.getPath().push(event.latLng);
      });

      this._mouseupEventListener = google.maps.event.addListenerOnce(this, 'mouseup', () => {
        this.enableMapDragAndScroll();
        this.disableDrawing();

        if (this._mousemoveEventListener) {
          google.maps.event.removeListener(this._mousemoveEventListener);
          this._mousemoveEventListener = null;
        }
        this._mousedownEventListener = null;
        this._mouseupEventListener = null;

        polyline.setMap(null);

        const polygon = new Polygon({paths: polyline.getPath()});
        const area = google.maps.geometry.spherical.computeArea(polygon.getPath());
        console.log('Polygon area (square meters):', area);

        // Store polygon data
        this._polygonArea = area;
        this._lastSelectionMode = 'LASSO';

        // Calculate center point
        const bounds = new google.maps.LatLngBounds();
        polygon.getPath().forEach((latLng: LatLng) => bounds.extend(latLng));
        const center = bounds.getCenter();

        // Store data globally for PDF export
        (window as any).currentMapData = {
          selectionMode: 'LASSO',
          polygonArea: area,
          centerPoint: { lat: center.lat(), lng: center.lng() }
        };
        console.log('Stored map data globally:', { selectionMode: 'LASSO', polygonArea: area, centerPoint: center });

        this._events$.next(new MapDrawEvent([polygon]));

        const newSelections: string[] = [];

        this.findLayer(LayerType.MARKER).objects.forEach((marker: Marker) => {
          const feature = marker['feature'];
          const latLng = new LatLng(feature.getGeometryAsLatLng());

          const containsLocation = google.maps.geometry.poly.containsLocation(latLng, polygon);
          if (containsLocation) {
            newSelections.push(marker['id']);
          }
        });

        this.selectAll(newSelections);
        setTimeout(() => {
          this.setSelectionMode(null);
        }, 200);
      });
    });
  }

  private handleRingSelection(radiusMilesFormControl: AbstractControl): void {
    this.disableMarkerClick();

    /*Draw a ring centered on the cursor until the user clicks*/
    this._mousemoveEventListener = google.maps.event.addListener(this, 'mousemove', (event: MapMouseEvent) => {
      const radiusMiles = radiusMilesFormControl.value;
          this._ringMiles = radiusMilesFormControl.value;

      const radiusMeters = milesToMeters(radiusMiles);

      if (this._ringCircle) {
        this._ringCircle.setOptions({
          center: event.latLng,
          radius: radiusMeters,
        });
      } else {
        this._ringCircle = new Circle({
          center: event.latLng,
          radius: radiusMeters,
          map: this,
          clickable: false,
          strokeColor: 'limegreen',
          fillColor: 'limegreen',
          fillOpacity: 0.5
        });
      }
    });

    google.maps.event.addListenerOnce(this, 'mouseup', (event: MapMouseEvent) => {
      this.enableMarkerClick();

      const radiusMiles = radiusMilesFormControl.value;
      const radiusMeters = milesToMeters(radiusMiles);

      const circle = new Circle({
        center: event.latLng,
        radius: radiusMeters
      });

      const polygon = GeometryUtil.googleCircleToGooglePolygon(circle);
      
      // Calculate perimeter (circumference of circle)
      const perimeterMeters = 2 * Math.PI * radiusMeters;
      const perimeterFeet = perimeterMeters * 3.28084;
      const perimeterMiles = perimeterMeters * 0.000621371;
      
      // Store ring data globally for PDF export
      (window as any).currentMapData = {
        selectionMode: 'RING',
        radiusMiles: radiusMiles,
        perimeterFeet: perimeterFeet,
        perimeterMiles: perimeterMiles,
        centerPoint: { lat: event.latLng.lat(), lng: event.latLng.lng() }
      };
      console.log('Stored ring data globally:', (window as any).currentMapData);

      const newSelections: string[] = [];

      this.findLayer(LayerType.MARKER).objects.forEach((marker: Marker) => {
        const feature = marker['feature'];
        const latLng = new LatLng(feature.getGeometryAsLatLng());

        const containsLocation = google.maps.geometry.poly.containsLocation(latLng, polygon);
        if (containsLocation) {
          newSelections.push(marker['id']);
        }
      });

      this.selectAll(newSelections);
      setTimeout(() => this.setSelectionMode(null), 50);
    });
  }

  private initEventListeners(): void {
    this.addListener('bounds_changed', () => {
      this._events$.next(new MapBoundsChangeEvent(this.bounds));
    });

    this.addListener('click', (event: MapMouseEvent) => {
      if (this._selectionMode === MapSelectionMode.MULTI_SELECT) {
        this.setSelectionMode(null);
      }
      this._events$.next(new MapClickEvent({
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }));
    });
  }

  private initLayers(): void {
    this.layers.push(new Layer(LayerType.THEMATIC, this));
    this.layers.push(new Layer(LayerType.MARKER, this));
  }

  private initMap(snapshot: MapSnapshot): void {
    this.validateKeyProvided(snapshot.options);
    this.initLayers();

    this._options = snapshot.options;
    this.setCenter(snapshot.center);
    this.setZoom(snapshot.zoom);
    this.getStreetView().setOptions({imageDateControl: true});
    this.setOptions({
      draggableCursor: ''
    });

    this.initEventListeners();
    this.initPlacesService();
    this.initSelections(snapshot);
  }

  private initPlacesService(): void {
    if (this._options.controlConfiguration.isGoogleSearchEnabled && !this._placesService) {
      this._placesService = new PlacesService(this);
    }
  }

  private initSelections(snapshot: MapSnapshot): void {
    if (snapshot.selections?.length) {
      this.selectAll(snapshot.selections);
    }
  }

  private resetDrawingOptions(): void {
    this.enableMarkerClick();
    this.enableMapDragAndScroll();
    this.disableDrawing();

    if (this._mousemoveEventListener) {
      google.maps.event.removeListener(this._mousemoveEventListener);
      this._mousemoveEventListener = null;
    }

    if (this._mousedownEventListener) {
      google.maps.event.removeListener(this._mousedownEventListener);
      this._mousedownEventListener = null;
    }

    if (this._mouseupEventListener) {
      google.maps.event.removeListener(this._mouseupEventListener);
      this._mouseupEventListener = null;
    }

    if (this._ringCircle) {
      this._ringCircle.setMap(null);
      this._ringCircle = null;
    }
  }

  private toggleSelection(id: string, isCtrlSelection: boolean, isExisting: boolean): void {
    if (isCtrlSelection) {
      if (isExisting) {
        this.removeSelection(id);
      } else if (id) {
        this.addSelection(id);
      }
    } else if (id) {
      this.addSelection(id);
    }
  }

  updateMarkers(): void {
    this.findLayer(LayerType.MARKER).objects.forEach((marker: any) => {
      const isSelected = this.isSelected(marker['id']);
      marker['ZIndex'] = (MarkerZIndexUtil.getMarkerZIndex(marker['feature'], isSelected));
      marker.content = MarkerUtil.createMarkerContentwithLabel(MarkerIconUtil.buildIcon(marker['feature'], isSelected).url);
    });
  }

  private validateKeyProvided(options: MtnMapOptions): void {
    if (!options.key) {
      console.warn('Map initialized without a key! Generating a key for you, but fix me!');
      options.key = Date.now().toString();
    }
  }
}
